import React, { useEffect, useState } from 'react';
import { Phone } from 'lucide-react';
import {
  FRONT_DESK_PHONE_DISPLAY,
  FRONT_DESK_HOURS,
  FRONT_DESK_LABEL,
  FRONT_DESK_TEL_HREF,
} from '../lib/siteContact';
import { logger } from '../lib/logger';

/**
 * FloatingCallPill (Phase 35)
 *
 * A compact, persistent floating button that sits in the bottom-right of
 * every public page — directly to the LEFT of the Quick Mindset Help
 * widget so it doesn't crowd the same hotspot.
 *
 * UX behavior:
 *  - On desktop: hover or focus reveals the full "Call Front Desk · number
 *    · hours" panel.
 *  - On mobile (<sm): the pill shrinks to an icon-only round button; the
 *    label panel opens on tap (toggle).
 *  - Dismissible with the small × — hides for 24h via localStorage.
 *  - Excluded on the same routes as the AI Coach trial popup (/app/*,
 *    /admin/*, /auth/*, the booking flow) so it doesn't compete with
 *    in-app UI or mid-conversion screens.
 */

const DISMISS_KEY = 'apex.frontDeskPill.dismissedUntil.v1';
const DISMISS_HOURS = 24;

const EXCLUDED_PREFIXES = [
  '/app',
  '/admin',
  '/auth',
  '/book',
  '/locker-room/welcome',
];

function isExcludedRoute(pathname) {
  return EXCLUDED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

function readDismissedUntil() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch (e) {
    logger.debug('FloatingCallPill dismiss read blocked', e);
    return 0;
  }
}

function setDismissedNow() {
  try {
    const until = Date.now() + DISMISS_HOURS * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
  } catch (e) {
    logger.debug('FloatingCallPill dismiss write blocked', e);
  }
}

export const FloatingCallPill = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Decide whether to render at all. Run once on mount + on route change.
  useEffect(() => {
    const evaluate = () => {
      if (typeof window === 'undefined') return;
      if (isExcludedRoute(window.location.pathname)) {
        setVisible(false);
        return;
      }
      const dismissedUntil = readDismissedUntil();
      if (dismissedUntil && Date.now() < dismissedUntil) {
        setVisible(false);
        return;
      }
      setVisible(true);
    };
    evaluate();
    // React-Router does a popstate / pushstate dance; listen for both so we
    // can re-evaluate without polluting routing internals.
    window.addEventListener('popstate', evaluate);
    return () => window.removeEventListener('popstate', evaluate);
  }, []);

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDismissedNow();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:bottom-[4.5rem] sm:right-5 z-[70] print:hidden"
      data-testid="floating-call-pill"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      <a
        href={FRONT_DESK_TEL_HREF}
        onFocus={() => setExpanded(true)}
        onBlur={() => setExpanded(false)}
        onClick={(e) => {
          // On mobile, first tap toggles the label panel; second tap triggers tel:
          if (window.matchMedia('(hover: none)').matches && !expanded) {
            e.preventDefault();
            setExpanded(true);
          }
        }}
        className={`group relative flex items-center gap-2 rounded-full bg-[#004C54] hover:bg-[#003a40] text-white shadow-lg shadow-[#004C54]/40 transition-all duration-200 pl-3 pr-${expanded ? '5' : '3'} h-11 ring-1 ring-[#4FBCC7]/40 hover:ring-[#4FBCC7]/70`}
        aria-label={`Call the ${FRONT_DESK_LABEL} at ${FRONT_DESK_PHONE_DISPLAY}`}
        data-testid="floating-call-pill-link"
      >
        <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
          <Phone className="h-3.5 w-3.5 text-[#4FBCC7]" />
        </span>
        {/* The label collapses on mobile + when not expanded on desktop. */}
        <span
          className={`overflow-hidden transition-all duration-200 ${
            expanded ? 'max-w-[280px] opacity-100' : 'max-w-0 opacity-0 sm:max-w-[160px] sm:opacity-100'
          }`}
        >
          <span className="block text-[12.5px] font-bold leading-tight whitespace-nowrap">
            {expanded ? `Call ${FRONT_DESK_PHONE_DISPLAY}` : 'Call Front Desk'}
          </span>
          <span className="block text-[10px] text-white/70 leading-tight whitespace-nowrap">
            {FRONT_DESK_HOURS}
          </span>
        </span>
      </a>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss call pill for 24 hours"
        className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#0a0d0e] text-white/60 hover:text-white hover:bg-[#1a1d1f] text-[11px] leading-none flex items-center justify-center shadow ring-1 ring-white/15 transition-opacity ${
          expanded ? 'opacity-100' : 'opacity-0 sm:opacity-0 pointer-events-none'
        }`}
        data-testid="floating-call-pill-dismiss"
      >
        ×
      </button>
    </div>
  );
};

export default FloatingCallPill;
